/**
 * InlineConfirm: an action that asks in place before it happens (cockatrice-ui `src/ui/InlineConfirm.jsx`, ported to
 * hyperscript; PLAN.md §0): the button turns into
 * "Delete for good? This can't be undone. [Cancel] [Delete]".
 * Lighter than a ConfirmDialog; use it inside lists and settings rows.
 * Focus moves to Cancel (the safe choice); Escape or Cancel puts the original button back and focuses it.
 */
import './css/inline-confirm.css';
import { createSignal, createUniqueId } from 'solid-js';
import Icon from './Icon.ts';
import type { IconName } from './Icon.ts';
import { uiText } from './text.ts';
import { component, el, when, whenSome } from './solid.ts';
import type { Child } from './solid.ts';

export interface InlineConfirmProps {
  /** Trigger label, and confirm label unless confirmLabel is set. Default "Delete". */
  readonly label?: string | undefined;
  /** Trigger icon name. */
  readonly icon?: IconName | undefined;
  /** The question. Default "Delete for good? This can't be undone." */
  readonly message?: string | undefined;
  /** Default: label. */
  readonly confirmLabel?: string | undefined;
  /** Default "Cancel". */
  readonly cancelLabel?: string | undefined;
  /** Default "danger". */
  readonly tone?: 'danger' | 'default' | undefined;
  /** Called when confirmed. */
  readonly onConfirm?: (() => void) | undefined;
  /** Disable the trigger. */
  readonly disabled?: boolean | undefined;
  /** Disable the confirm answer while asking (the action can't run now, as when the work is read-only); Cancel stays. */
  readonly confirmDisabled?: boolean | undefined;
  /** Start in the asking state (previews). */
  readonly defaultConfirming?: boolean | undefined;
  /** Replace the trigger's classes. */
  readonly triggerClass?: string | undefined;
  /** The trigger's accessible name when its words need the row's context ("Remove a@example.com" for "Remove"). */
  readonly triggerLabel?: string | undefined;
  /** IDs of the help that describes the trigger (`aria-describedby`). */
  readonly triggerDescribedBy?: string | undefined;
}

/** An action that asks in place before it happens. */
export default function InlineConfirm(props: InlineConfirmProps): Child {
  const id = `ui-ic-${createUniqueId()}`;
  const [asking, setAsking] = createSignal(props.defaultConfirming === true);
  let triggerEl: HTMLButtonElement | undefined;
  let cancelEl: HTMLButtonElement | undefined;
  const tone = (): 'danger' | 'default' => props.tone ?? 'danger';

  // The focus moves once the swap is made (Solid makes it at once; a microtask, not a frame, so nothing can come
  // between), and after either answer it goes back to the trigger, so it is never lost (PLAN.md §3, step 1; A24).
  const ask = (): void => {
    setAsking(true);
    queueMicrotask(() => cancelEl?.focus());
  };
  const back = (): void => {
    setAsking(false);
    queueMicrotask(() => triggerEl?.focus());
  };
  const go = (): void => {
    setAsking(false);
    props.onConfirm?.();
    queueMicrotask(() => {
      if (triggerEl?.isConnected === true) triggerEl.focus();
    });
  };

  return when(
    () => asking(),
    () =>
      el(
        'div',
        {
          class: () => `ui-inline-confirm ui-inline-confirm--${tone()}`,
          role: 'group',
          'aria-labelledby': id,
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              back();
            }
          },
        },
        el(
          'p',
          { id, class: 'ui-inline-confirm__msg' },
          when(
            () => tone() === 'danger',
            () => component(Icon, { name: 'warn', size: 'sm', class: 'ui-inline-confirm__icon' }),
          ),
          el('span', {}, () => props.message ?? uiText()('ui.inline-confirm.message')),
        ),
        el(
          'div',
          { class: 'ui-inline-confirm__actions' },
          el(
            'button',
            {
              ref: (node: HTMLButtonElement) => {
                cancelEl = node;
              },
              type: 'button',
              class: 'ui-inline-confirm__btn',
              // Each answer is described by the question, so it is read as the focus lands (deep-fix r7).
              'aria-describedby': id,
              onClick: back,
            },
            () => props.cancelLabel ?? uiText()('ui.inline-confirm.cancel'),
          ),
          el(
            'button',
            { type: 'button', class: () => `ui-inline-confirm__btn ui-inline-confirm__btn--${tone()}`, 'aria-describedby': id, disabled: () => props.confirmDisabled, onClick: go },
            () => props.confirmLabel ?? props.label ?? uiText()('ui.inline-confirm.delete'),
          ),
        ),
      ),
    () =>
      el(
        'button',
        {
          ref: (node: HTMLButtonElement) => {
            triggerEl = node;
          },
          type: 'button',
          class: () => props.triggerClass ?? `ui-inline-confirm__trigger ui-inline-confirm__trigger--${tone()}`,
          'aria-label': () => props.triggerLabel,
          'aria-describedby': () => props.triggerDescribedBy,
          disabled: () => props.disabled,
          onClick: ask,
        },
        whenSome(
          () => props.icon,
          (icon) =>
            component(Icon, {
              get name() {
                return icon();
              },
              size: 'sm',
            }),
        ),
        () => props.label ?? uiText()('ui.inline-confirm.delete'),
      ),
  );
}
